/**
 * Helper functions for handling multilingual content in forms
 */

/**
 * Get the appropriate text based on current language and multilingual object
 * @param {Object|string} textObj - Either a multilingual object {en: "...", tr: "..."} or a string
 * @param {string} currentLanguage - Current language code (e.g., 'en', 'tr')
 * @param {string} fallbackLanguage - Fallback language code (default: 'en')
 * @returns {string} - The text in the current language or fallback
 */
export const getMultilingualText = (textObj, currentLanguage = 'en', fallbackLanguage = 'en') => {
    // If textObj is already a string, return it as is
    if (typeof textObj === 'string') {
        return textObj;
    }

    // If textObj is not an object, return empty string
    if (!textObj || typeof textObj !== 'object') {
        return '';
    }

    // Try to get text in current language first
    if (textObj[currentLanguage]) {
        return textObj[currentLanguage];
    }

    // Fallback to fallback language
    if (textObj[fallbackLanguage]) {
        return textObj[fallbackLanguage];
    }

    // If neither language is available, return the first available value
    const availableLanguages = Object.keys(textObj);
    if (availableLanguages.length > 0) {
        return textObj[availableLanguages[0]];
    }

    // If no text is available, return empty string
    return '';
};

/**
 * Get multilingual text for form labels
 * @param {Object|string} labelObj - Label object or string
 * @param {string} currentLanguage - Current language code
 * @returns {string} - The label text
 */
export const getFormLabel = (labelObj, currentLanguage = 'en') => {
    return getMultilingualText(labelObj, currentLanguage);
};

/**
 * Get multilingual text for form placeholders
 * @param {Object|string} placeholderObj - Placeholder object or string
 * @param {string} currentLanguage - Current language code
 * @returns {string} - The placeholder text
 */
export const getFormPlaceholder = (placeholderObj, currentLanguage = 'en') => {
    return getMultilingualText(placeholderObj, currentLanguage);
};

/**
 * Get multilingual text for form options
 * @param {Array} options - Array of option objects or strings
 * @param {string} currentLanguage - Current language code
 * @returns {Array} - Array of option texts
 */
export const getFormOptions = (options = [], currentLanguage = 'en') => {
    if (!Array.isArray(options)) {
        return [];
    }

    return options.map(option => {
        if (typeof option === 'string') {
            return option;
        }
        return getMultilingualText(option, currentLanguage);
    });
};

/**
 * Get form options with English values for select components
 * @param {Array} options - Array of option objects or strings
 * @param {string} currentLanguage - Current language code
 * @returns {Array} - Array of {value, label} objects where value is always English
 */
export const getFormOptionsWithEnglishValues = (options = [], currentLanguage = 'en') => {
    if (!Array.isArray(options)) {
        return [];
    }

    return options.map(option => {
        if (typeof option === 'string') {
            return { value: option, label: option };
        }

        // Handle structured options with separate label and value
        if (option.label && option.value) {
            const englishLabel = getMultilingualText(option.label, 'en');
            const displayLabel = getMultilingualText(option.label, currentLanguage);
            return { value: option.value, label: displayLabel };
        }

        // Handle multilingual options where the whole option is multilingual
        const englishValue = getMultilingualText(option, 'en');
        const displayLabel = getMultilingualText(option, currentLanguage);

        return { value: englishValue, label: displayLabel };
    });
};

/**
 * Get English value from multilingual option
 * @param {Object|string} option - Option object or string
 * @returns {string} - English value
 */
export const getEnglishValue = (option) => {
    if (typeof option === 'string') {
        return option;
    }
    return getMultilingualText(option, 'en');
};

/**
 * Get multilingual text for form instructions
 * @param {Object|string} instructionsObj - Instructions object or string
 * @param {string} currentLanguage - Current language code
 * @returns {string} - The instructions text
 */
export const getFormInstructions = (instructionsObj, currentLanguage = 'en') => {
    return getMultilingualText(instructionsObj, currentLanguage);
};

/**
 * Process raw_data array to convert multilingual labels to strings
 * Always returns English labels regardless of current language
 * @param {Array} rawData - Array of field objects with multilingual labels
 * @param {string} currentLanguage - Current language code (ignored, always uses 'en')
 * @returns {Array} - Processed array with English string labels
 */
export const processRawDataLabels = (rawData = [], currentLanguage = 'en') => {
    if (!Array.isArray(rawData)) {
        return [];
    }

    return rawData.map(field => {
        if (!field || typeof field !== 'object') {
            return field;
        }

        // If label is an object, always convert it to English string
        if (field.label && typeof field.label === 'object') {
            return {
                ...field,
                label: getMultilingualText(field.label, 'en') // Always use English
            };
        }

        // If label is already a string or doesn't exist, return as is
        return field;
    });
};

/**
 * Process a single field object to convert multilingual label to string
 * Always returns English labels regardless of current language
 * @param {Object} field - Field object with potential multilingual label
 * @param {string} currentLanguage - Current language code (ignored, always uses 'en')
 * @returns {Object} - Processed field with English string label
 */
export const processFieldLabel = (field, currentLanguage = 'en') => {
    if (!field || typeof field !== 'object') {
        return field;
    }

    // If label is an object, always convert it to English string
    if (field.label && typeof field.label === 'object') {
        return {
            ...field,
            label: getMultilingualText(field.label, 'en') // Always use English
        };
    }

    // If label is already a string or doesn't exist, return as is
    return field;
};
